import { Group, Stack, Text } from "@mantine/core"
import { Badge, DateCell, Missing } from "@tfg/ui"
import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { Property } from "~/components/Property.tsx"
import { DocumentIFrame } from "~/pages/Brand/Products/Details/CustomFieldGraph/CustomFieldProductsDetailsInfo"
import { datapointResponseVersionSnapshotDtoScopeEnum } from "~/gen/api/types/DatapointResponseVersionSnapshotDto.ts"
import type { DatapointResponseVersionSnapshotDto } from "~/gen/api/types/DatapointResponseVersionSnapshotDto.ts"
import type { ResolveDatapointResponseCanonicalValueResponseDto } from "~/gen/api/types/ResolveDatapointResponseCanonicalValueResponseDto.ts"

import type { DatapointScopeTarget } from "./datapoint-response-versions.types.ts"

const AlignedValue = ({ children }: { children: ReactNode }) => (
  <Group justify="flex-end" w="100%">
    {children}
  </Group>
)

type DatapointResponseSnapshotDetailsProps = {
  readonly snapshot: DatapointResponseVersionSnapshotDto
  readonly target: DatapointScopeTarget
  readonly canonicalValue: ResolveDatapointResponseCanonicalValueResponseDto["canonicalValue"]
}

export const DatapointResponseSnapshotDetails = ({
  snapshot,
  target,
  canonicalValue,
}: DatapointResponseSnapshotDetailsProps) => {
  const { t } = useTranslation("products")

  const isCanonical = canonicalValue?.id === snapshot.id

  return (
    <Stack gap="sm">
      <Group gap="xs">
        {isCanonical && (
          <Badge
            size="xs"
            color="gray"
            text={t("details.responseVersions.canonical")}
          />
        )}
        {snapshot.isFirstHand ? (
          <Badge
            size="xs"
            color="gray"
            text={t("details.responseVersions.firstHand")}
          />
        ) : (
          <Badge
            size="xs"
            color="gray"
            text={t("details.responseVersions.onBehalf")}
          />
        )}
      </Group>

      {target.scope ===
        datapointResponseVersionSnapshotDtoScopeEnum.CustomFieldValue && (
        <>
          <Property
            label={t("details.responseVersions.fields.value")}
            value={
              <AlignedValue>
                <Text size="sm" fw={600}>
                  {snapshot.value || <Missing />}
                </Text>
              </AlignedValue>
            }
            size="sm"
          />
          {snapshot.documentId && (
            <Stack gap="xs">
              <Text size="sm">
                {t("details.responseVersions.fields.document")}
              </Text>
              <DocumentIFrame documentId={snapshot.documentId} height="240px" />
            </Stack>
          )}
        </>
      )}

      {target.scope ===
        datapointResponseVersionSnapshotDtoScopeEnum.Operation && (
        <>
          <Property
            label={t("traceability.supplierCard.productReference")}
            value={
              <AlignedValue>
                <Text size="sm">{snapshot.reference || <Missing />}</Text>
              </AlignedValue>
            }
            size="sm"
          />
          <Property
            label={t("details.properties.purchaseOrder")}
            value={
              <AlignedValue>
                <Text size="sm">
                  {snapshot.purchaseOrderRef || <Missing />}
                </Text>
              </AlignedValue>
            }
            size="sm"
          />
          <Property
            label={t("traceability.supplierCard.company")}
            value={
              <AlignedValue>
                <Text size="sm">
                  {snapshot.authorOrganization.name || <Missing />}
                </Text>
              </AlignedValue>
            }
            size="sm"
          />
        </>
      )}

      <Property
        label={t("details.responseVersions.fields.updatedAt")}
        value={
          <AlignedValue>
            <DateCell date={snapshot.updatedAt} size="sm" fw={600} />
          </AlignedValue>
        }
        size="sm"
      />
    </Stack>
  )
}
